import { useEffect, useRef, useState } from "react";

interface FormulaInputProps {
  value: string;
  onChange: (value: string) => void;
  fields: { label: string }[];
  placeholder?: string;
  rows?: number;
}

const DATE_FUNCTIONS = [
  { name: "today()", insert: "today()" },
  { name: "now()", insert: "now()" },
  { name: "dateDiff(a, b, unit)", insert: "dateDiff(" },
  { name: "dateAdd(date, n, unit)", insert: "dateAdd(" },
  { name: "dateFormat(date, fmt)", insert: "dateFormat(" },
];

/**
 * Textarea with ${...} field-name autocomplete.
 * When the user types "${" (or "${<partial>"), a dropdown shows matching field names.
 */
export default function FormulaInput({
  value,
  onChange,
  fields,
  placeholder = 'e.g., ${Price} * ${Quantity}',
  rows = 3,
}: FormulaInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [mode, setMode] = useState<"field" | "function">("field");
  // Track the start position of the current token being typed
  const tokenStartRef = useRef<number>(-1);

  const getActiveToken = (text: string, cursor: number): { token: string; start: number } | null => {
    // Walk back from cursor to find an unclosed ${
    const before = text.slice(0, cursor);
    const lastOpen = before.lastIndexOf("${");
    if (lastOpen === -1) return null;
    const afterOpen = before.slice(lastOpen + 2);
    // If there's a closing } between the ${ and cursor, the token is closed
    if (afterOpen.indexOf("}") !== -1) return null;
    return { token: afterOpen, start: lastOpen };
  };

  // Detect plain-text function prefix (no ${ wrapper) e.g. "dateDi"
  const getFunctionToken = (text: string, cursor: number): { token: string; start: number } | null => {
    const before = text.slice(0, cursor);
    const match = before.match(/([a-zA-Z][a-zA-Z0-9]*)$/);
    if (!match) return null;
    return { token: match[1], start: cursor - match[1].length };
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newVal = e.target.value;
    onChange(newVal);

    const cursor = e.target.selectionStart ?? newVal.length;
    const active = getActiveToken(newVal, cursor);
    if (active) {
      tokenStartRef.current = active.start;
      const partial = active.token.toLowerCase();
      const matches = fields
        .map((f) => f.label)
        .filter((l) => l.toLowerCase().indexOf(partial) !== -1);
      setSuggestions(matches);
      setActiveSuggestion(0);
      setMode("field");
    } else {
      const fnToken = getFunctionToken(newVal, cursor);
      if (fnToken && fnToken.token.length >= 2) {
        const partial = fnToken.token.toLowerCase();
        const matches = DATE_FUNCTIONS
          .filter((f) => f.name.toLowerCase().indexOf(partial) !== -1)
          .map((f) => f.name);
        if (matches.length > 0) {
          tokenStartRef.current = fnToken.start;
          setSuggestions(matches);
          setActiveSuggestion(0);
          setMode("function");
        } else {
          setSuggestions([]);
          tokenStartRef.current = -1;
        }
      } else {
        setSuggestions([]);
        tokenStartRef.current = -1;
      }
    }
  };

  const applySuggestion = (suggestion: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart ?? value.length;

    if (mode === "field") {
      const active = getActiveToken(value, cursor);
      if (!active) return;
      // Replace from ${ up to cursor with ${label}
      const before = value.slice(0, active.start);
      const after = value.slice(cursor);
      const newVal = before + "${" + suggestion + "}" + after;
      onChange(newVal);
      setSuggestions([]);
      tokenStartRef.current = -1;
      setTimeout(() => {
        const newCursor = before.length + suggestion.length + 3;
        ta.focus();
        ta.setSelectionRange(newCursor, newCursor);
      }, 0);
    } else {
      // Function mode - replace the typed prefix with the insert string
      const fnEntry = DATE_FUNCTIONS.find((f) => f.name === suggestion);
      const insert = fnEntry ? fnEntry.insert : suggestion;
      const before = value.slice(0, tokenStartRef.current);
      const after = value.slice(cursor);
      const newVal = before + insert + after;
      onChange(newVal);
      setSuggestions([]);
      tokenStartRef.current = -1;
      setTimeout(() => {
        const newCursor = before.length + insert.length;
        ta.focus();
        ta.setSelectionRange(newCursor, newCursor);
      }, 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestion((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestion((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      applySuggestion(suggestions[activeSuggestion]);
    } else if (e.key === "Escape") {
      setSuggestions([]);
    }
  };

  // Close suggestions on outside click
  useEffect(() => {
    const close = () => setSuggestions([]);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        className="w-full border rounded px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        rows={rows}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        spellCheck={false}
      />
      {suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 mt-1 w-full bg-white border rounded shadow-lg max-h-48 overflow-y-auto text-sm"
          onMouseDown={(e) => e.preventDefault()} // prevent textarea blur
        >
          {suggestions.map((label, idx) => (
            <li
              key={label}
              className={`px-3 py-1.5 cursor-pointer font-mono ${
                idx === activeSuggestion
                  ? "bg-primary text-white"
                  : "hover:bg-gray-100 text-gray-800"
              }`}
              onMouseDown={() => applySuggestion(label)}
            >
              <span className="text-gray-400 select-none">${"{"}</span>
              {label}
              <span className="text-gray-400 select-none">{"}"}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
